import SectionCard from "@/components/SectionCard";
import KpiCard from "@/components/KpiCard";
import AreaChart from "@/components/charts/AreaChart";
import BarChart from "@/components/charts/BarChart";
import DonutChart from "@/components/charts/DonutChart";
import { contactabilidad, getContactoAgregadoByPeriodo, getContactoMensualByPeriodo } from "@/mocks/data";
import { useFilters } from "@/context/FiltersContext";
import { Users, Mail, Phone, ShieldCheck, MailOpen, MousePointerClick, MailX, Send, Eye, MessageSquare } from "lucide-react";
import { formatNum, formatPct } from "@/lib/constants";

export default function Contactabilidad() {
  const d = contactabilidad;
  const { filters } = useFilters();

  // Datos reales de contacto por período (motor de comunicaciones)
  const contactoAgg = getContactoAgregadoByPeriodo(filters.periodo);

  return (
    <div className="space-y-6" data-testid="contactabilidad-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Contactabilidad</h1>
        <p className="text-sm text-slate-500 mt-1">Maestro unificado de contacto cruzando 8 fuentes de datos · {filters.periodo}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testid="kpi-cob-total" label="Cobertura total" value={formatNum(d.cobertura_total)} suffix="pacientes" icon={Users} accent="violet" />
        <KpiCard testid="kpi-cob-email" label="Con email" value={formatPct(d.pct_email)} icon={Mail} accent="blue" delta={0.5} />
        <KpiCard testid="kpi-cob-telefono" label="Con teléfono" value={formatPct(d.pct_telefono)} icon={Phone} accent="emerald" delta={0.3} />
        <KpiCard testid="kpi-cob-ambos" label="Con ambos canales" value={formatPct(d.pct_ambos)} icon={ShieldCheck} accent="amber" delta={0.8} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <SectionCard title="Tendencia de cobertura" subtitle="Últimos 6 meses · ⚠️ Datos estimados" testid="tendencia-cobertura">
            <AreaChart
              categories={d.tendencia.map((t) => t.mes)}
              series={[
                { name: "Email %", data: d.tendencia.map((t) => t.email) },
                { name: "Teléfono %", data: d.tendencia.map((t) => t.telefono) },
              ]}
              colors={["#7C3AED", "#06B6D4"]}
              height={320}
            />
          </SectionCard>
        </div>
        <SectionCard title="Consentimiento por canal" subtitle="% pacientes opt-in · ⚠️ Datos estimados" testid="consentimiento">
          <DonutChart
            labels={["Email", "WhatsApp", "SMS", "Llamada"]}
            series={[d.consentimiento.email_opt_in, d.consentimiento.whatsapp_opt_in, d.consentimiento.sms_opt_in, d.consentimiento.call_opt_in]}
            colors={["#7C3AED", "#10B981", "#F59E0B", "#3B82F6"]}
            height={320}
          />
        </SectionCard>
      </div>

      <SectionCard title="Pacientes por fuente de datos" subtitle="8 fuentes unificadas" testid="fuentes-datos">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">
                <th className="py-3 px-4">Fuente</th>
                <th className="py-3 px-4 text-right">Pacientes</th>
                <th className="py-3 px-4">Calidad</th>
                <th className="py-3 px-4">Participación</th>
              </tr>
            </thead>
            <tbody>
              {d.fuentes.map((f, i) => {
                const max = Math.max(...d.fuentes.map((x) => x.pacientes));
                return (
                  <tr key={i} className="border-b border-slate-100 hover:bg-slate-50 transition">
                    <td className="py-3 px-4 font-semibold text-slate-800">{f.nombre}</td>
                    <td className="py-3 px-4 text-right tabular-nums text-slate-700">{formatNum(f.pacientes)}</td>
                    <td className="py-3 px-4">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                        f.calidad === "Alta" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
                      }`}>{f.calidad.toUpperCase()}</span>
                    </td>
                    <td className="py-3 px-4 w-64">
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-violet-500 to-blue-500 rounded-full"
                             style={{ width: `${(f.pacientes / max) * 100}%` }} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </SectionCard>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <SectionCard title="Gap de contactabilidad" testid="gap-contacto">
          <div className="space-y-4">
            <div className="rounded-xl p-5 bg-gradient-to-br from-rose-50 to-white border border-rose-200">
              <div className="text-xs font-semibold text-rose-600 uppercase tracking-wider">Sin datos de contacto</div>
              <div className="text-4xl font-bold text-rose-700 mt-2 tabular-nums">{formatNum(d.sin_datos)}</div>
              <div className="text-sm text-slate-600 mt-1">pacientes no contactables</div>
            </div>
            <div className="rounded-xl p-5 bg-gradient-to-br from-amber-50 to-white border border-amber-200">
              <div className="text-xs font-semibold text-amber-600 uppercase tracking-wider">Solo un canal</div>
              <div className="text-4xl font-bold text-amber-700 mt-2 tabular-nums">{formatNum(d.cobertura_total - d.con_ambos - d.sin_datos)}</div>
              <div className="text-sm text-slate-600 mt-1">pacientes con cobertura limitada</div>
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Volumen por fuente" testid="fuentes-bar">
          <BarChart
            categories={d.fuentes.map((f) => f.nombre)}
            series={[{ name: "Pacientes", data: d.fuentes.map((f) => f.pacientes) }]}
            colors={["#7C3AED", "#8B5CF6", "#6366F1", "#3B82F6", "#06B6D4", "#14B8A6", "#10B981", "#64748B"]}
            horizontal
            distributed
            height={380}
          />
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <SectionCard title="Engagement Email" subtitle={`Motor de comunicaciones · ${filters.periodo} · Datos reales`} testid="engagement-email">
          <div className="space-y-5">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
                <MailOpen className="w-5 h-5 text-violet-600" />
              </div>
              <div className="flex-1">
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Open Rate (destinatarios)</div>
                <div className="text-3xl font-bold text-slate-900 tabular-nums">{contactoAgg.email.open_rate}%</div>
                <div className="mt-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-violet-500 rounded-full" style={{ width: `${contactoAgg.email.open_rate}%` }} />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <MousePointerClick className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Click Rate (destinatarios)</div>
                <div className="text-3xl font-bold text-slate-900 tabular-nums">{contactoAgg.email.click_rate}%</div>
                <div className="mt-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full" style={{ width: `${contactoAgg.email.click_rate}%` }} />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                <Mail className="w-5 h-5 text-slate-600" />
              </div>
              <div className="flex-1">
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Emails enviados</div>
                <div className="text-3xl font-bold text-slate-900 tabular-nums">{formatNum(contactoAgg.email.enviados)}</div>
                <div className="text-xs text-slate-500 mt-1 tabular-nums">~{formatNum(contactoAgg.email.destinatarios_est)} destinatarios</div>
              </div>
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Engagement WhatsApp" subtitle={`Motor de comunicaciones · ${filters.periodo} · Datos reales`} testid="engagement-whatsapp">
          <div className="space-y-5">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <Send className="w-5 h-5 text-emerald-600" />
              </div>
              <div className="flex-1">
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Delivery Rate</div>
                <div className="text-3xl font-bold text-slate-900 tabular-nums">
                  {contactoAgg.wsp.enviados > 0 ? ((contactoAgg.wsp.entregados / contactoAgg.wsp.enviados) * 100).toFixed(1) : "0.0"}%
                </div>
                <div className="mt-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${contactoAgg.wsp.enviados > 0 ? (contactoAgg.wsp.entregados / contactoAgg.wsp.enviados) * 100 : 0}%` }} />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-cyan-100 flex items-center justify-center">
                <Eye className="w-5 h-5 text-cyan-600" />
              </div>
              <div className="flex-1">
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Read Rate (teléfonos)</div>
                <div className="text-3xl font-bold text-slate-900 tabular-nums">{contactoAgg.wsp.read_rate}%</div>
                <div className="mt-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-cyan-500 rounded-full" style={{ width: `${contactoAgg.wsp.read_rate}%` }} />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-slate-600" />
              </div>
              <div className="flex-1">
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Mensajes enviados</div>
                <div className="text-3xl font-bold text-slate-900 tabular-nums">{formatNum(contactoAgg.wsp.enviados)}</div>
                <div className="text-xs text-slate-500 mt-1 tabular-nums">~{formatNum(contactoAgg.wsp.telefonos_est)} teléfonos</div>
              </div>
            </div>
          </div>
        </SectionCard>
      </div>
    </div>
  );
}
